import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import {
  DragDropItem,
  DragDropList,
  DraggableInner,
} from '@/components/ui/custom/dataInput/DragDrop';
import { Button } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import { useCombinationQuestions } from '@/data/hook/question/answers/useCombinationQuestions';
import { useModalDnd } from '@/data/hook/shared/dnd/useModalDnd';
import type { DraggableChildrenFn } from '@hello-pangea/dnd';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CombinationFormFields } from './CombinationForm';
import QuestionColumnItem from './QuestionColumnItem';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';

type QuestionColumnFormProps = {
  identifier: string;
};

export default function QuestionColumnForm({
  identifier,
}: QuestionColumnFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });
  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  const { watch } = useFormContext<CombinationFormFields>();
  const { isLimitedEdition, isPublished } = usePedWorkspace();
  const liveItems = watch('questions');

  const {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleDelete,
    handleDragEnd,
    isNewItem,
  } = useCombinationQuestions();

  const renderClone: DraggableChildrenFn = (provided, snapshot, rubric) => {
    const index = rubric.source.index;
    const item = liveItems?.[index] ?? fields[index];

    return (
      <DraggableInner
        provided={provided}
        snapshot={snapshot}
        className={{ base: 'items-center gap-3 mb-4' }}
      >
        <QuestionColumnItem
          item={item}
          index={index}
          onUpdateText={text => handleUpdateText(index, text)}
          onUpdateMedia={(file, altText) =>
            handleUpdateMedia(index, file, altText)
          }
          onDelete={() => handleDelete(index)}
        />
      </DraggableInner>
    );
  };

  const { containerRef, getContainerForClone } = useModalDnd();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{t('mainColumnTitle')}</h3>
        <ConditionalRenderer condition={!!error?.root?.message} fallback={null}>
          <Fieldset.Label className="text-error">
            {error?.root?.message}
          </Fieldset.Label>
        </ConditionalRenderer>
      </div>

      <ConditionalRenderer condition={fields.length}>
        <div ref={containerRef} className="relative">
          <DragDropList
            groupId={`combination-questions-${identifier}`}
            onDragEnd={handleDragEnd}
            renderClone={renderClone}
            getContainerForClone={getContainerForClone}
            className="gap-0 relative"
          >
            {fields.map((field, index) => (
              <DragDropItem
                key={field.fieldId}
                id={field.fieldId}
                index={index}
                className={{ base: 'items-center gap-3 mb-4' }}
                disabled={isLimitedEdition}
                tooltip={
                  isLimitedEdition
                    ? editRulesT(
                        isPublished ? 'cantModifyPublished' : 'cantModify',
                      )
                    : undefined
                }
              >
                <QuestionColumnItem
                  item={liveItems?.[index] ?? field}
                  index={index}
                  autoFocus={isNewItem({ index })}
                  onUpdateText={text => handleUpdateText(index, text)}
                  onUpdateMedia={(file, altText) =>
                    handleUpdateMedia(index, file, altText)
                  }
                  onDelete={() => handleDelete(index)}
                />
              </DragDropItem>
            ))}
          </DragDropList>
        </div>
      </ConditionalRenderer>
      <PedWorkspaceGuard className="w-fit" placement="right">
        <Button
          type="button"
          variant="soft"
          color="primary"
          className="self-start"
          onClick={handleAdd}
          disabled={isLimitedEdition}
        >
          <PlusIcon className="size-3.5" />
          {t('newItem')}
        </Button>
      </PedWorkspaceGuard>
    </div>
  );
}
